import {
  canCompileExplorationPipeline,
  compileExplorationPipeline,
  computeRenderedGraphCounts,
} from '@/helpers/explorationPipeline.helpers';
import {
  hasActiveGraphFilters,
  type GraphFiltersByLabel,
} from '@/helpers/graphFilter.helpers';
import { buildEntityListPipeline } from '@/helpers/pipelineBuilders.helpers';
import { getDisplayNodeLabel } from '@/helpers/graph.helpers';
import type { DataTableFilterMeta } from 'primereact/datatable';
import type { ViewMode } from '@/interfaces/graphViewStates.interface';
import type {
  Pipeline,
  PipelineParamValue,
  RunPipelinesRequest,
} from '@/interfaces/pipelines.interface';
import type { ReasonerResponseType } from '@/interfaces/reasoner.interface';
import type {
  GraphData,
  GraphNode,
  SavedGraphLayout,
  SavedNodePosition,
} from '@/types/graph.types';
import type {
  ExplorationOp,
  ExplorationSession,
} from '@/types/exploration.types';
import type { ReMapViewMode } from '@/types/remapViewMode.types';
import type { TableNodesInfo } from '@/types/table.types';
import type { AnalysisCardConfig, PipelineCardConfig } from '@/types/dashboard';
import type { AnalysisRecipeState } from '@/types/analysisRecipe.types';
import { deserializeAnalysisRecipeState } from '@/helpers/analysisRecipeState.helpers';
import {
  buildAnalysisCardMetadata,
  type AnalysisPublicationSurface,
} from '@/helpers/analysisPublication.helpers';

// v2 adds the optional `layout` field (node positions + viewport) to
// client_metadata. Views saved at v1 simply have no layout and fall back to
// the force-directed auto-layout on restore.
export const GRAPH_VIEW_STATE_SCHEMA_VERSION = 2;

export type GraphViewClientMode =
  | 'schema'
  | 'query'
  | 'singleNode'
  | 'reasoner'
  | 'exploration';

export interface GraphViewClientMetadata {
  mode: GraphViewClientMode;
  exploration: ExplorationSession;
  query?: { cypher: string };
  singleNode?: { node: GraphNode };
  reasoner?: {
    reasonerResponse: ReasonerResponseType | null;
    availableIds: string[];
    reasonerGraphNodes: GraphNode[] | null;
    reasonerGraphRelationships: GraphData['relationships'] | null;
    validatedNodeIds: string[] | null;
    isFromReasoner: boolean;
  };
  table?: {
    entityType: string;
    merge?: boolean;
    activeSchemaTab?: string;
  };
  /**
   * Per-label table column filters active when the view was saved. Restoring a
   * view reapplies these so the table *and* the graph (which mirrors the table
   * filters) show the same filtered subset the view was named after.
   */
  filters?: GraphFiltersByLabel;
  /**
   * Canvas layout (node positions + viewport) captured from NVL at save time.
   * Restored after the graph renders so the arrangement matches what the user
   * saved. Absent on v1 views and whenever the canvas had no rendered nodes.
   */
  layout?: SavedGraphLayout;
  analysisRecipe?: AnalysisRecipeState;
  analysisPlanId?: string;
}

export interface ParsedSavedGraphView {
  id: string;
  name: string;
  description: string | null;
  viewMode: ViewMode;
  planId: string | null;
  requestPayload: RunPipelinesRequest | null;
  clientMetadata: GraphViewClientMetadata;
  schemaVersion: number;
}

export interface CaptureCurrentViewInput {
  viewMode: ReMapViewMode;
  exploration: ExplorationSession;
  graphData: GraphData;
  tableNodes: TableNodesInfo;
  queryCypher?: string | null;
  nodeToOpen?: GraphNode | null;
  reasonerSession?: {
    reasonerResponse: ReasonerResponseType | null;
    availableIds: Set<string>;
    reasonerGraphNodes: GraphNode[] | null;
    reasonerGraphRelationships: GraphData['relationships'] | null;
    validatedNodeIds: string[] | null;
    isFromReasoner: boolean;
  };
  activeSchemaTab?: string;
  filtersByLabel?: GraphFiltersByLabel;
  /** Live NVL canvas layout to persist; null/empty when unavailable. */
  layout?: SavedGraphLayout | null;
  analysisRecipe?: AnalysisRecipeState;
  analysisPipeline?: Pipeline;
  analysisPlanId?: string;
}

export interface CaptureCurrentViewResult {
  suggestedName: string;
  view_mode: ViewMode;
  request_payload: RunPipelinesRequest | null;
  client_metadata: GraphViewClientMetadata;
  node_count: number;
  relationship_count: number;
  plan_id: string | null;
  schema_version: number;
}

function hasExpandOrRemoveOps(ops: ExplorationOp[]): boolean {
  return ops.some(
    (op) => op.kind === 'expandToLabel' || op.kind === 'removeNode',
  );
}

function mapViewModeToClientMode(viewMode: ReMapViewMode): GraphViewClientMode {
  switch (viewMode.kind) {
    case 'schema':
      return 'schema';
    case 'query':
      return 'query';
    case 'singleNode':
      return 'singleNode';
    case 'reasoner':
      return 'reasoner';
    case 'exploration':
      return 'exploration';
    default:
      return 'schema';
  }
}

function mapViewModeToApiMode(
  viewMode: ReMapViewMode,
  ops: ExplorationOp[],
): ViewMode {
  if (
    viewMode.kind === 'exploration' ||
    hasExpandOrRemoveOps(ops)
  ) {
    return 'exploration';
  }
  return mapViewModeToClientMode(viewMode);
}

export function buildSaveName(
  ops: ExplorationOp[],
  graphNodes: GraphNode[],
  viewMode: ReMapViewMode,
): string {
  const last = ops.at(-1);
  if (last) {
    switch (last.kind) {
      case 'seedSingleNode':
        return `Opened ${last.label}`;
      case 'seedFromGraph': {
        const labels = Object.keys(last.idsByLabel);
        return labels.length === 1
          ? `Opened ${labels[0]} graph`
          : `Opened graph (${labels.join(', ')})`;
      }
      case 'seedByIds':
        return `Opened ${last.ids.length} nodes`;
      case 'expandToLabel':
        return `Expanded ${last.sourceLabel} to ${last.targetLabel}`;
      case 'removeNode': {
        const node = graphNodes.find((n) => n.id === last.nodeId);
        const label = getDisplayNodeLabel(node?.labels);
        return label !== 'Unknown' ? `Removed ${label}` : 'Removed node';
      }
    }
  }

  switch (viewMode.kind) {
    case 'query':
      return 'Query result';
    case 'reasoner':
      return 'Reasoner subgraph';
    case 'singleNode': {
      const label = getDisplayNodeLabel(viewMode.node.labels);
      return label !== 'Unknown' ? `Opened ${label}` : 'Opened node';
    }
    case 'exploration':
      return 'Graph exploration';
    case 'schema':
    default:
      return 'Schema view';
  }
}

export function captureCurrentView(
  input: CaptureCurrentViewInput,
): CaptureCurrentViewResult {
  const {
    viewMode,
    exploration,
    graphData,
    tableNodes,
    queryCypher,
    nodeToOpen,
    reasonerSession,
    activeSchemaTab,
    filtersByLabel,
    layout,
    analysisRecipe,
    analysisPipeline,
    analysisPlanId,
  } = input;

  const clientMode = mapViewModeToClientMode(viewMode);
  const clientMetadata: GraphViewClientMetadata = {
    mode: clientMode,
    exploration: { ops: exploration.ops, planId: exploration.planId },
    table: {
      entityType: tableNodes.entityType,
      merge: tableNodes.merge,
      activeSchemaTab,
    },
  };

  if (hasActiveGraphFilters(filtersByLabel)) {
    clientMetadata.filters = filtersByLabel;
  }

  // Persist the canvas layout only when NVL gave us real node positions;
  // an empty positions array carries no information and is left off so the
  // view falls back to auto-layout on restore.
  if (layout && layout.positions.length > 0) {
    clientMetadata.layout = layout;
  }
  if (analysisRecipe) {
    clientMetadata.analysisRecipe = analysisRecipe;
    clientMetadata.analysisPlanId = analysisPlanId;
  }

  if (clientMode === 'query' && queryCypher) {
    clientMetadata.query = { cypher: queryCypher };
  }

  if (clientMode === 'singleNode' && nodeToOpen) {
    clientMetadata.singleNode = { node: nodeToOpen };
  }

  if (clientMode === 'reasoner' && reasonerSession) {
    clientMetadata.reasoner = {
      reasonerResponse: reasonerSession.reasonerResponse,
      availableIds: Array.from(reasonerSession.availableIds),
      reasonerGraphNodes: reasonerSession.reasonerGraphNodes,
      reasonerGraphRelationships: reasonerSession.reasonerGraphRelationships,
      validatedNodeIds: reasonerSession.validatedNodeIds,
      isFromReasoner: reasonerSession.isFromReasoner,
    };
  }

  // Only persist a compiled pipeline when the op-log is actually compilable.
  // A log containing only `removeNode` ops would throw in the compiler.
  const requestPayload: RunPipelinesRequest | null = analysisPipeline
    ? { pipelines: [analysisPipeline] }
    : canCompileExplorationPipeline(exploration.ops)
      ? { pipelines: [compileExplorationPipeline(exploration.ops)] }
      : null;

  // Persist the counts of the *rendered* graph: re-apply the op-log's removeNode
  // deletions so a view's saved node/relationship totals match what restoring it
  // actually shows. The backend's project_graph can re-introduce excluded nodes
  // as neighbours, and the client prunes them again via filterGraphByRemovedIds;
  // counting the raw graphData would otherwise overstate the view's size.
  const renderedCounts = computeRenderedGraphCounts(
    graphData,
    exploration.ops,
    hasActiveGraphFilters(filtersByLabel) ? filtersByLabel : undefined,
  );

  return {
    suggestedName: buildSaveName(exploration.ops, graphData.nodes, viewMode),
    view_mode: mapViewModeToApiMode(viewMode, exploration.ops),
    request_payload: requestPayload,
    client_metadata: clientMetadata,
    node_count: renderedCounts.nodeCount,
    relationship_count: renderedCounts.relationshipCount,
    plan_id: analysisPlanId ?? exploration.planId ?? null,
    schema_version: GRAPH_VIEW_STATE_SCHEMA_VERSION,
  };
}

export interface BuildPipelineCardConfigOptions {
  renderMode: PipelineCardConfig['renderMode'];
  /** Chart mode only: which node label's rows become the tabular dataset. */
  targetLabel?: string;
  /** Per-label table filters to reproduce a filtered projection. */
  filters?: GraphFiltersByLabel;
}

/**
 * Derives a {@link PipelineCardConfig} from a captured reMap view so it can be
 * stored on a dashboard "Pipeline" card and re-run on render.
 *
 * Returns `null` when the current view has no compilable pipeline
 * (`captured.request_payload === null`, i.e. `canCompileExplorationPipeline`
 * returned false). Callers should disable/hide the "Send to dashboard" action
 * in that case, since there is nothing to re-run.
 */
export function buildPipelineCardConfig(
  captured: CaptureCurrentViewResult,
  options: BuildPipelineCardConfigOptions,
): PipelineCardConfig | null {
  if (!captured.request_payload) {
    return null;
  }

  const config: PipelineCardConfig = {
    requestPayload: captured.request_payload,
    renderMode: options.renderMode,
    planId: captured.plan_id,
    schemaVersion: captured.schema_version,
    sourceViewMode: captured.view_mode,
  };

  if (options.renderMode === 'chart' && options.targetLabel) {
    config.targetLabel = options.targetLabel;
  }

  const filters = options.filters ?? captured.client_metadata.filters;
  if (filters && hasActiveGraphFilters(filters)) {
    config.filters = filters;
  }

  return config;
}

/**
 * Config for a schema-view graph card. Schema graphs have no compiled pipeline;
 * the card re-fetches the live project schema via `/graph/query` on render (see
 * the `'schema-graph'` branch in `useDashboardChartCard`).
 */
export function buildSchemaGraphCardConfig(): PipelineCardConfig {
  return {
    source: 'schema-graph',
    renderMode: 'graph',
    sourceViewMode: 'schema',
  };
}

export interface BuildAnalysisCardConfigOptions {
  surface: AnalysisPublicationSurface;
  /**
   * `'chart'` stores the Table pipeline the card pages or materialises;
   * `'graph'` stores the induced-graph pipeline.
   */
  renderMode: PipelineCardConfig['renderMode'];
  /** Table cards page; charts and KPIs need the whole result before rendering. */
  rowsPolicy: AnalysisCardConfig['rowsPolicy'];
  /** Plan id from the sheet's last run, reused as a warm-cache hint. */
  planId?: string | null;
  /**
   * Values a pinned Analysis chart froze. They become card-owned overrides: the
   * card keeps answering the pinned question while its other controls stay
   * bindable to dashboard globals.
   */
  pinnedParams?: Record<string, PipelineParamValue>;
}

/**
 * Config for an Analysis Sheet card. Unlike an exploration card this stores the
 * sheet's own publication pipeline plus the metadata a card needs to reproduce
 * the sheet: its output columns, its row key, whether rows are nodes or groups,
 * and the control values in force when it was published.
 *
 * Returns null when the requested output has no pipeline — a grouped or
 * rolled-up recipe has no graph to draw — so a caller can never fall back to
 * publishing an unrelated schema or exploration view in its place.
 */
export function buildAnalysisCardConfig(
  options: BuildAnalysisCardConfigOptions,
): PipelineCardConfig | null {
  const { surface, renderMode, rowsPolicy, planId, pinnedParams } = options;
  const pipeline =
    renderMode === 'graph' ? surface.graphPipeline : surface.tablePipeline;
  if (!pipeline) return null;
  const paramValues = { ...surface.paramValues, ...(pinnedParams ?? {}) };
  return {
    source: 'analysis',
    analysis: buildAnalysisCardMetadata(surface, rowsPolicy, pinnedParams),
    requestPayload: {
      pipelines: [pipeline],
      ...(Object.keys(surface.pipelineColumnLabels).length > 0
        ? { column_labels: surface.pipelineColumnLabels }
        : {}),
    },
    renderMode,
    ...(surface.sourceLabel && renderMode === 'chart'
      ? { targetLabel: surface.sourceLabel }
      : {}),
    ...(Object.keys(paramValues).length > 0
      ? { currentParamValues: paramValues }
      : {}),
    // A graph card runs its own pipeline, so the table run's plan does not
    // apply to it.
    ...(renderMode === 'chart' && planId ? { planId } : {}),
    schemaVersion: GRAPH_VIEW_STATE_SCHEMA_VERSION,
    sourceViewMode: 'schema',
  };
}

/** Active server-side sort captured from a schema-view entity table tab. */
export interface EntityTableSort {
  sortField: string | null;
  sortOrder: 1 | -1;
  sortNumeric?: boolean;
  sortDateTime?: boolean;
}

export interface BuildEntityListCardConfigOptions {
  /** Node label whose rows the card projects. */
  targetLabel: string;
  /** Active column filters for that label's table tab. */
  filters?: DataTableFilterMeta;
  /** Active server-side sort for that label, if the user sorted the table. */
  sort?: EntityTableSort | null;
}

/**
 * Config for a schema-view table card: a `remap_entity_list` pipeline for a
 * single label that reproduces the active sort + filters so the dashboard card
 * matches what the user saw. Row limit/offset use the pipeline defaults, so the
 * card is a live "top N by this sort + filter" projection rather than a frozen
 * page (exact pagination is intentionally out of scope).
 */
export function buildEntityListCardConfig(
  options: BuildEntityListCardConfigOptions,
): PipelineCardConfig {
  const { targetLabel, filters, sort } = options;
  const pipeline = buildEntityListPipeline({
    entityType: targetLabel,
    filters,
    ...(sort?.sortField
      ? {
          sortField: sort.sortField,
          sortOrder: sort.sortOrder,
          sortNumeric: sort.sortNumeric,
          sortDateTime: sort.sortDateTime,
        }
      : {}),
  });
  return {
    source: 'entity-list',
    renderMode: 'chart',
    targetLabel,
    requestPayload: { pipelines: [pipeline] },
    sourceViewMode: 'schema',
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Defensively parse a persisted canvas layout. Tolerates missing/legacy shapes
 * (returns null) and drops any position entry that isn't a finite {id,x,y}, so
 * a partially-corrupt payload degrades to auto-layout rather than throwing.
 */
function parseSavedLayout(raw: unknown): SavedGraphLayout | null {
  if (!isRecord(raw) || !Array.isArray(raw.positions)) return null;

  const positions: SavedNodePosition[] = [];
  for (const entry of raw.positions) {
    if (
      isRecord(entry) &&
      typeof entry.id === 'string' &&
      Number.isFinite(entry.x) &&
      Number.isFinite(entry.y)
    ) {
      positions.push({
        id: entry.id,
        x: entry.x as number,
        y: entry.y as number,
      });
    }
  }
  if (positions.length === 0) return null;

  // `scale`/`pan` from views saved by older builds are ignored on purpose:
  // the restored graph is fitted to the container instead.
  return { positions };
}

export function parseSavedView(record: {
  id: string;
  name: string;
  description: string | null;
  view_mode: ViewMode;
  plan_id: string | null;
  request_payload: Record<string, unknown> | null;
  client_metadata: Record<string, unknown>;
  schema_version: number;
}): ParsedSavedGraphView {
  const raw = record.client_metadata;
  const mode = (raw.mode as GraphViewClientMode) ?? 'schema';

  const explorationRaw = isRecord(raw.exploration) ? raw.exploration : {};
  const exploration: ExplorationSession = {
    ops: Array.isArray(explorationRaw.ops)
      ? (explorationRaw.ops as ExplorationOp[])
      : [],
    planId:
      typeof explorationRaw.planId === 'string'
        ? explorationRaw.planId
        : record.plan_id ?? undefined,
  };

  const clientMetadata: GraphViewClientMetadata = {
    mode,
    exploration,
    table: isRecord(raw.table)
      ? {
          entityType:
            typeof raw.table.entityType === 'string' ? raw.table.entityType : '',
          merge:
            typeof raw.table.merge === 'boolean' ? raw.table.merge : undefined,
          activeSchemaTab:
            typeof raw.table.activeSchemaTab === 'string'
              ? raw.table.activeSchemaTab
              : undefined,
        }
      : undefined,
  };

  if (isRecord(raw.filters)) {
    clientMetadata.filters = raw.filters as GraphFiltersByLabel;
  }

  const layout = parseSavedLayout(raw.layout);
  if (layout) {
    clientMetadata.layout = layout;
  }

  if (isRecord(raw.query) && typeof raw.query.cypher === 'string') {
    clientMetadata.query = { cypher: raw.query.cypher };
  }

  if (isRecord(raw.singleNode) && isRecord(raw.singleNode.node)) {
    clientMetadata.singleNode = {
      node: raw.singleNode.node as GraphNode,
    };
  }

  if (isRecord(raw.reasoner)) {
    clientMetadata.reasoner = {
      reasonerResponse:
        (raw.reasoner.reasonerResponse as ReasonerResponseType | null) ?? null,
      availableIds: Array.isArray(raw.reasoner.availableIds)
        ? (raw.reasoner.availableIds as string[])
        : [],
      reasonerGraphNodes: Array.isArray(raw.reasoner.reasonerGraphNodes)
        ? (raw.reasoner.reasonerGraphNodes as GraphNode[])
        : null,
      reasonerGraphRelationships: Array.isArray(
        raw.reasoner.reasonerGraphRelationships,
      )
        ? (raw.reasoner.reasonerGraphRelationships as GraphData['relationships'])
        : null,
      validatedNodeIds: Array.isArray(raw.reasoner.validatedNodeIds)
        ? (raw.reasoner.validatedNodeIds as string[])
        : null,
      isFromReasoner: Boolean(raw.reasoner.isFromReasoner),
    };
  }
  if (isRecord(raw.analysisRecipe)) {
    try {
      clientMetadata.analysisRecipe = deserializeAnalysisRecipeState(
        raw.analysisRecipe,
      );
      clientMetadata.analysisPlanId =
        typeof raw.analysisPlanId === 'string'
          ? raw.analysisPlanId
          : undefined;
    } catch {
      // A malformed Analysis payload must not prevent the underlying graph view
      // from restoring; the recipe is omitted and the UI starts clean.
    }
  }

  return {
    id: record.id,
    name: record.name,
    description: record.description,
    viewMode: record.view_mode,
    planId: record.plan_id,
    requestPayload: record.request_payload as RunPipelinesRequest | null,
    clientMetadata,
    schemaVersion: record.schema_version,
  };
}

export interface RestoreExplorationOptions {
  /** Saved graph snapshot used when seed ops cannot rebuild from fallback alone. */
  seedGraphHint?: GraphData | null;
}

export function needsPipelineReplay(
  session: ExplorationSession,
  options?: RestoreExplorationOptions,
): boolean {
  const hasExpandOrRemove = session.ops.some(
    (op) => op.kind === 'expandToLabel' || op.kind === 'removeNode',
  );
  if (hasExpandOrRemove) return true;

  const hasSeedByIds = session.ops.some(
    (op) => op.kind === 'seedByIds' && op.idsByLabel,
  );
  if (hasSeedByIds) return true;

  const hasSeedFromGraph = session.ops.some((op) => op.kind === 'seedFromGraph');
  if (hasSeedFromGraph) {
    const hint = options?.seedGraphHint;
    return !(hint && hint.nodes.length > 0);
  }

  /*
   * A lone `seedSingleNode` op carries a label and an id and nothing else, so
   * rebuilding the graph from the op-log can only produce `{ id }` — see the
   * synthesised fallback in `buildSeedGraphFromOps`. That renders as a circle
   * with nothing written in it, above a table whose only column is ID, which is
   * what reloading the page on an open record used to give.
   *
   * The record has to be fetched, not reconstructed. Skipped only when the hint
   * already carries the real node: a node holding nothing but its id is the
   * thing being avoided, so it does not count as carried.
   */
  const singleNodeSeed = session.ops.find(
    (op): op is Extract<ExplorationOp, { kind: 'seedSingleNode' }> =>
      op.kind === 'seedSingleNode',
  );
  if (singleNodeSeed) {
    const seeded = options?.seedGraphHint?.nodes.find(
      (node) => node.id === singleNodeSeed.nodeId,
    );
    return !(seeded && Object.keys(seeded.properties ?? {}).length > 1);
  }

  return false;
}

export function buildSeedGraphFromOps(
  ops: ExplorationOp[],
  fallbackGraph: GraphData,
  seedGraphHint?: GraphData | null,
): { graph: GraphData; seedOps: ExplorationOp[] } {
  const seedOps = ops.filter(
    (op) =>
      op.kind === 'seedSingleNode' ||
      op.kind === 'seedFromGraph' ||
      op.kind === 'seedByIds',
  );

  const singleSeed = ops.find(
    (op): op is Extract<ExplorationOp, { kind: 'seedSingleNode' }> =>
      op.kind === 'seedSingleNode',
  );

  if (singleSeed) {
    const node =
      fallbackGraph.nodes.find((n) => n.id === singleSeed.nodeId) ??
      seedGraphHint?.nodes.find((n) => n.id === singleSeed.nodeId) ??
      ({
        id: singleSeed.nodeId,
        labels: [singleSeed.label],
        properties: { id: singleSeed.nodeId },
      } as GraphNode);
    return {
      graph: { nodes: [node], relationships: [] },
      seedOps,
    };
  }

  const seedFromGraph = ops.find(
    (op): op is Extract<ExplorationOp, { kind: 'seedFromGraph' }> =>
      op.kind === 'seedFromGraph',
  );

  if (seedFromGraph) {
    if (seedGraphHint && seedGraphHint.nodes.length > 0) {
      return {
        graph: {
          nodes: seedGraphHint.nodes,
          relationships: seedGraphHint.relationships ?? [],
        },
        seedOps,
      };
    }
    if (fallbackGraph.nodes.length > 0) {
      return { graph: fallbackGraph, seedOps };
    }
  }

  return { graph: fallbackGraph, seedOps };
}
